<template>
  <div>
    <v-toolbar
      flat
      class="mb-4"
    >
      <v-btn
        icon
        @click="$router.push({ name: 'group_reserve' })"
      >
        <v-icon>{{ icons.mdiArrowLeft }}</v-icon>
      </v-btn>
      <v-toolbar-title>Conta master do grupo</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-chip
        v-if="account"
        :color="accountStatus.color"
        :text-color="accountStatus.textColor"
      >
        {{ accountStatus.label }}
      </v-chip>
    </v-toolbar>

    <v-skeleton-loader
      v-if="loading"
      type="article, table"
    ></v-skeleton-loader>

    <template v-else-if="group">
      <v-card
        v-if="!account"
        class="mb-4"
      >
        <v-card-title>Abrir conta master</v-card-title>
        <v-card-text>
          <p class="mb-4">
            A conta reúne pagamentos e checkouts enviados pelos quartos deste grupo.
          </p>
          <v-radio-group
            v-model="responsible.type"
            row
          >
            <v-radio
              :disabled="!group.company"
              label="Empresa do grupo"
              value="company"
            ></v-radio>
            <v-radio
              :disabled="groupGuests.length === 0"
              label="Hóspede do grupo"
              value="guest"
            ></v-radio>
          </v-radio-group>
          <v-text-field
            v-if="responsible.type === 'company' && group.company"
            :value="group.company.fantasy_name"
            label="Empresa responsável"
            outlined
            readonly
          ></v-text-field>
          <v-select
            v-else-if="responsible.type === 'guest' && groupGuests.length"
            v-model="responsible.item"
            :items="groupGuests"
            item-text="name"
            item-value="id"
            return-object
            label="Hóspede responsável"
            outlined
          ></v-select>
          <v-alert
            v-else
            dense
            outlined
            type="warning"
            class="mb-0"
          >
            Este grupo não possui empresa nem hóspede cadastrado para assumir a conta master. Cadastre ou vincule um responsável antes de abrir a conta.
          </v-alert>
          <div class="summary-label mt-4">
            O que a master cobre (pré-seleção ao mover um quarto)
          </div>
          <v-radio-group
            v-model="coverageMode"
            row
            class="mt-0"
          >
            <v-radio
              label="Hospedagem"
              value="lodging"
            ></v-radio>
            <v-radio
              label="Hospedagem + extras"
              value="lodging_extras"
            ></v-radio>
          </v-radio-group>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-btn
            color="primary"
            :loading="creating"
            :disabled="!responsible.item"
            @click="createAccount()"
          >
            Abrir conta master
          </v-btn>
        </v-card-actions>
      </v-card>

      <template v-else>
        <v-row>
          <v-col
            cols="12"
            md="7"
          >
            <v-card height="100%">
              <v-card-title>Resumo financeiro</v-card-title>
              <v-card-text>
                <v-row dense>
                  <v-col
                    cols="6"
                    sm="4"
                  >
                    <div class="summary-label">
                      Lançado até agora
                    </div>
                    <div class="summary-value">
                      R$ {{ money(summary.current_charges) }}
                    </div>
                  </v-col>
                  <v-col
                    cols="6"
                    sm="4"
                  >
                    <div class="summary-label">
                      Previsto da estadia
                    </div>
                    <div class="summary-value">
                      R$ {{ money(summary.projected_charges) }}
                    </div>
                  </v-col>
                  <v-col
                    cols="6"
                    sm="4"
                  >
                    <div class="summary-label">
                      Transferido
                    </div>
                    <div class="summary-value">
                      R$ {{ money(summary.transferred_charges) }}
                    </div>
                  </v-col>
                  <v-col
                    cols="6"
                    sm="4"
                  >
                    <div class="summary-label">
                      Pagamentos
                    </div>
                    <div class="summary-value">
                      R$ {{ money(totalPayments) }}
                    </div>
                  </v-col>
                  <v-col
                    cols="6"
                    sm="4"
                  >
                    <div class="summary-label">
                      Saldo atual
                    </div>
                    <div :class="['summary-value', currentBalanceClass]">
                      R$ {{ money(summary.current_balance) }}
                    </div>
                  </v-col>
                  <v-col
                    cols="6"
                    sm="4"
                  >
                    <div class="summary-label">
                      Saldo previsto
                    </div>
                    <div class="summary-value">
                      R$ {{ money(summary.projected_balance) }}
                    </div>
                  </v-col>
                </v-row>
                <v-divider class="my-3" />
                <div class="d-flex align-center mb-2">
                  <div class="summary-label mb-0">
                    Hospedagem × Consumo
                  </div>
                  <v-spacer />
                  <v-btn-toggle
                    v-model="breakdownView"
                    mandatory
                    dense
                  >
                    <v-btn
                      small
                      value="current"
                    >
                      Atual
                    </v-btn>
                    <v-btn
                      small
                      value="projected"
                    >
                      Previsto
                    </v-btn>
                  </v-btn-toggle>
                </div>
                <v-simple-table dense>
                  <tbody>
                    <tr
                      v-for="bucket in breakdownBuckets"
                      :key="bucket.key"
                    >
                      <td>{{ bucket.label }}</td>
                      <td class="text-right">
                        R$ {{ money(summaryBreakdown[bucket.key]) }}
                      </td>
                    </tr>
                    <tr class="font-weight-bold">
                      <td>Total</td>
                      <td class="text-right">
                        R$ {{ money(summaryBreakdown.total) }}
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col
            cols="12"
            md="5"
          >
            <v-card height="100%">
              <v-card-title>Responsável</v-card-title>
              <v-card-text>
                <div class="text-subtitle-1">
                  {{ responsibleName }}
                </div>
                <div class="text-caption">
                  {{ account.responsible_type === 'company' ? 'Empresa' : 'Hóspede' }}
                </div>
              </v-card-text>
              <v-card-text class="pt-0">
                <v-row dense>
                  <v-col
                    v-if="account.status === 'open'"
                    cols="6"
                  >
                    <v-btn
                      color="primary"
                      outlined
                      block
                      @click="openPaymentDialog('master_advance')"
                    >
                      <v-icon left>
                        {{ icons.mdiCashPlus }}
                      </v-icon>
                      Adiantamento
                    </v-btn>
                  </v-col>
                  <v-col
                    v-if="account.status === 'open' && effectiveBalance > 0 && !hasRoomInPreview"
                    cols="6"
                  >
                    <v-btn
                      color="info"
                      outlined
                      block
                      @click="openPaymentDialog('master_closing')"
                    >
                      <v-icon left>
                        {{ icons.mdiCashCheck }}
                      </v-icon>
                      Quitar saldo
                    </v-btn>
                  </v-col>
                  <v-col
                    v-if="account.status === 'open'"
                    cols="6"
                  >
                    <v-btn
                      color="secondary"
                      outlined
                      block
                      @click="adjustmentDialog = true"
                    >
                      Ajustes
                    </v-btn>
                  </v-col>
                  <v-col
                    v-if="account.status === 'open'"
                    cols="6"
                  >
                    <v-btn
                      color="secondary"
                      outlined
                      block
                      @click="openCompanyDialog()"
                    >
                      Trocar empresa
                    </v-btn>
                  </v-col>
                  <v-col
                    v-if="account.status === 'open'"
                    cols="6"
                  >
                    <v-btn
                      color="success"
                      block
                      @click="closeDialog = true"
                    >
                      Fechar conta
                    </v-btn>
                  </v-col>
                  <v-col
                    v-if="account.status === 'closed_pending_audit'"
                    cols="6"
                  >
                    <v-btn
                      color="warning"
                      block
                      :loading="reopening"
                      @click="reopenAccount()"
                    >
                      Reabrir conta
                    </v-btn>
                  </v-col>
                  <v-col
                    v-if="account.status === 'closed_pending_audit'"
                    cols="6"
                  >
                    <v-btn
                      color="success"
                      block
                      :loading="sendingToGestao"
                      @click="sendToGestao()"
                    >
                      Enviar para Gestão
                    </v-btn>
                  </v-col>
                  <v-col
                    v-if="['closed_pending_audit', 'sent'].includes(account.status)"
                    cols="6"
                  >
                    <v-btn
                      color="primary"
                      outlined
                      block
                      :loading="printingVoucher"
                      @click="printVoucher()"
                    >
                      <v-icon left>
                        {{ icons.mdiPrinter }}
                      </v-icon>
                      Voucher
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-card class="mt-4">
          <v-card-title>Quartos do grupo</v-card-title>
          <v-chip-group
            v-model="stateFilter"
            mandatory
            active-class="primary--text"
            class="px-4"
          >
            <v-chip
              v-for="option in stateFilters"
              :key="option.value"
              :value="option.value"
              small
              filter
            >
              {{ option.label }}
            </v-chip>
          </v-chip-group>
          <v-data-table
            :headers="roomHeaders"
            :items="filteredRooms"
            item-key="room_key"
            show-expand
            single-expand
            dense
            hide-default-footer
          >
            <template v-slot:expanded-item="{ headers, item }">
              <td
                :colspan="headers.length"
                class="py-2"
              >
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Categoria</th>
                      <th class="text-right">
                        Atual
                      </th>
                      <th class="text-right">
                        Previsto
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="bucket in breakdownBuckets"
                      :key="bucket.key"
                    >
                      <td>{{ bucket.label }}</td>
                      <td class="text-right">
                        R$ {{ money(item.breakdown.current[bucket.key]) }}
                      </td>
                      <td class="text-right">
                        R$ {{ money(item.breakdown.projected[bucket.key]) }}
                      </td>
                    </tr>
                    <tr class="font-weight-bold">
                      <td>Total</td>
                      <td class="text-right">
                        R$ {{ money(item.breakdown.current.total) }}
                      </td>
                      <td class="text-right">
                        R$ {{ money(item.breakdown.projected.total) }}
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
                <div
                  v-if="item.accommodation_id"
                  class="mt-2"
                >
                  <v-btn
                    small
                    text
                    color="primary"
                    :loading="loadingLines === item.accommodation_id"
                    @click="loadRoomLines(item)"
                  >
                    {{ roomLines[item.accommodation_id] ? 'Atualizar lançamentos' : 'Ver lançamentos' }}
                  </v-btn>
                  <v-simple-table
                    v-if="roomLines[item.accommodation_id]"
                    dense
                  >
                    <thead>
                      <tr>
                        <th>Categoria</th>
                        <th>Descrição</th>
                        <th class="text-right">
                          Qtd
                        </th>
                        <th class="text-right">
                          Valor
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(line, index) in roomLines[item.accommodation_id]"
                        :key="index"
                      >
                        <td>{{ bucketLabel(line.bucket) }}</td>
                        <td>{{ line.description }}</td>
                        <td class="text-right">
                          {{ line.quantity }}
                        </td>
                        <td class="text-right">
                          R$ {{ money(line.total) }}
                        </td>
                      </tr>
                      <tr v-if="!roomLines[item.accommodation_id].length">
                        <td colspan="4">
                          Sem lançamentos.
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </div>
              </td>
            </template>
            <template v-slot:item.guests="{ item }">
              {{ item.guests.join(', ') || '-' }}
            </template>
            <template v-slot:item.financial_state="{ item }">
              <v-chip
                x-small
                :color="roomFinancialState(item).color"
                :text-color="roomFinancialState(item).textColor"
              >
                {{ roomFinancialState(item).label }}
              </v-chip>
            </template>
            <template v-slot:item.current_charges="{ item }">
              R$ {{ money(item.current_charges) }}
            </template>
            <template v-slot:item.projected_charges="{ item }">
              R$ {{ money(item.projected_charges) }}
            </template>
            <template v-slot:item.payments="{ item }">
              R$ {{ money(item.payments) }}
            </template>
            <template v-slot:item.current_balance="{ item }">
              R$ {{ money(item.current_balance) }}
            </template>
            <template v-slot:item.projected_balance="{ item }">
              R$ {{ money(item.projected_balance) }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn
                v-if="item.financial_state === 'transferred' && account.status === 'open'"
                small
                text
                color="warning"
                @click="openDetachDialog(item)"
              >
                Retornar avulsa
              </v-btn>
            </template>
          </v-data-table>
        </v-card>

        <v-card class="mt-4">
          <v-card-title>Pagamentos da conta</v-card-title>
          <v-data-table
            :headers="paymentHeaders"
            :items="payments"
            dense
            hide-default-footer
          >
            <template v-slot:item.amount="{ item }">
              R$ {{ money(item.amount) }}
            </template>
            <template v-slot:item.paid_at="{ item }">
              {{ dateTime(item.paid_at) }}
            </template>
            <template v-slot:item.allocation_type="{ item }">
              {{ paymentOrigin(item.allocation_type) }}
            </template>
            <template v-slot:item.method="{ item }">
              {{ paymentMethod(item.method) }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn
                v-if="account.status === 'open' && ['master_advance', 'master_closing'].includes(item.allocation_type)"
                small
                text
                color="error"
                @click="openRemovePaymentDialog(item)"
              >
                Apagar
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </template>
    </template>

    <v-dialog
      v-model="paymentDialog"
      max-width="640"
    >
      <v-card>
        <BarTitleDialog
          :title="payment.payment_type === 'master_closing' ? 'Quitar saldo da conta master' : 'Adiantamento da conta master'"
          @close="paymentDialog = false"
        ></BarTitleDialog>
        <v-card-text class="pt-5">
          <div class="text-caption grey--text mb-2">
            Forma de pagamento
          </div>
          <v-row dense>
            <v-col
              v-for="option in paymentMethodOptions"
              :key="option.value"
              cols="4"
              sm="3"
            >
              <v-card
                outlined
                class="payment-method-card"
                :class="{ selected: payment.method === option.value }"
                @click="payment.method = option.value"
              >
                <v-card-text class="text-center py-3">
                  <img
                    v-if="option.image"
                    :src="option.image"
                    width="28"
                    class="mb-1"
                    alt=""
                  >
                  <v-icon
                    v-else
                    :color="option.color"
                    large
                  >
                    {{ option.icon }}
                  </v-icon>
                  <div class="text-caption mt-1">
                    {{ option.text }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <v-alert
            v-if="payment.payment_type === 'master_closing'"
            dense
            outlined
            type="info"
            class="mb-4"
          >
            Saldo a quitar: R$ {{ money(effectiveBalance) }}. Se o pagamento for em mais de uma forma, registre uma de cada vez — o valor abaixo pode ser menor que o saldo.
          </v-alert>
          <vuetify-money
            v-model="payment.value"
            outlined
            label="Valor"
          ></vuetify-money>
          <v-select
            v-model="payment.payment_type"
            :items="paymentTypes"
            outlined
            disabled
            label="Tipo de pagamento"
          ></v-select>
          <SelectPix
            v-if="payment.method === 'pix'"
            v-model="payment.pix_id"
            :error_messages="pixError"
          ></SelectPix>
          <v-text-field
            v-if="payment.method === 'invoice'"
            v-model="payment.date_invoice"
            outlined
            type="date"
            label="Vencimento"
            :error-messages="invoiceDateError"
          ></v-text-field>
          <v-text-field
            v-if="['credit_card', 'debit_card', 'other'].includes(payment.method)"
            v-model="payment.code_operation"
            outlined
            :label="['credit_card', 'debit_card'].includes(payment.method) ? 'Código de autorização' : 'Código da operação'"
            :error-messages="codeOperationError"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="paymentDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            x-large
            :loading="savingPayment"
            :disabled="!payment.value || !payment.method"
            @click="addPayment()"
          >
            Registrar R$ {{ money(payment.value) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="closeDialog"
      max-width="520"
    >
      <v-card>
        <BarTitleDialog
          title="Fechar conta master"
          @close="closeDialog = false"
        ></BarTitleDialog>
        <v-card-text class="pt-5">
          <v-alert
            v-if="summary.current_balance > 0"
            dense
            outlined
            type="warning"
            class="mb-4"
          >
            A conta ainda possui saldo em aberto de R$ {{ money(summary.current_balance) }}. Ela ficará aguardando auditoria mesmo assim.
          </v-alert>
          <v-row dense>
            <v-col cols="6">
              <div class="summary-label">
                Saldo atual
              </div>
              <div :class="['summary-value', currentBalanceClass]">
                R$ {{ money(summary.current_balance) }}
              </div>
            </v-col>
            <v-col cols="6">
              <div class="summary-label">
                Pagamentos
              </div>
              <div class="summary-value">
                R$ {{ money(totalPayments) }}
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="success"
            x-large
            :loading="closing"
            @click="confirmCloseAccount()"
          >
            Confirmar fechamento
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="adjustmentDialog"
      max-width="580"
    >
      <v-card>
        <BarTitleDialog
          title="Ajustes da conta master"
          @close="adjustmentDialog = false"
        ></BarTitleDialog>
        <v-card-text class="pt-5">
          <vuetify-money
            v-model="adjustments.discount"
            outlined
            label="Desconto"
          ></vuetify-money>
          <v-textarea
            v-model="adjustments.discount_obs"
            outlined
            rows="2"
            label="Justificativa do desconto"
          ></v-textarea>
          <vuetify-money
            v-model="adjustments.additional"
            outlined
            label="Acréscimo"
          ></vuetify-money>
          <v-textarea
            v-model="adjustments.additional_obs"
            outlined
            rows="2"
            label="Justificativa do acréscimo"
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="adjustmentDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            :loading="savingAdjustments"
            @click="saveAdjustments()"
          >
            Salvar ajustes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="companyDialog"
      max-width="580"
    >
      <v-card>
        <BarTitleDialog
          title="Trocar empresa responsável"
          @close="companyDialog = false"
        ></BarTitleDialog>
        <v-card-text class="pt-5">
          <v-alert
            dense
            text
            type="warning"
          >
            A nova empresa substitui a atual na conta master, no grupo e em todas as reservas e acomodações do grupo.
          </v-alert>
          <AutocompleteSearchCompany
            v-model="newCompany"
          ></AutocompleteSearchCompany>
          <v-alert
            v-if="newCompany && newCompany.allow_billing === false && billingReserveCount > 0"
            dense
            text
            type="error"
          >
            Esta empresa não permite faturamento. O faturamento no checkout será desativado em {{ billingReserveCount }} reserva(s) do grupo.
          </v-alert>
          <v-textarea
            v-model="companyJustification"
            outlined
            rows="2"
            label="Justificativa"
            hide-details
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="companyDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            :loading="savingCompany"
            :disabled="!newCompany || !newCompany.id || companyJustification.trim().length < 3"
            @click="saveCompany()"
          >
            Trocar empresa
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="removePaymentDialog"
      max-width="520"
    >
      <v-card>
        <BarTitleDialog
          title="Apagar pagamento"
          @close="removePaymentDialog = false"
        ></BarTitleDialog>
        <v-card-text class="pt-5">
          <p>
            O pagamento de R$ {{ removePaymentTarget ? money(removePaymentTarget.amount) : '' }} ({{ removePaymentTarget ? paymentOrigin(removePaymentTarget.allocation_type) : '' }}) será removido da conta master.
          </p>
          <v-textarea
            v-model="removePaymentJustification"
            outlined
            rows="3"
            label="Justificativa"
            :rules="[value => !!value || 'Informe a justificativa']"
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="removePaymentDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="error"
            :loading="removingPayment"
            :disabled="!removePaymentJustification"
            @click="removePayment()"
          >
            Apagar pagamento
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="detachDialog"
      max-width="520"
    >
      <v-card>
        <BarTitleDialog
          title="Retornar para conta avulsa"
          @close="detachDialog = false"
        ></BarTitleDialog>
        <v-card-text class="pt-5">
          <p>O quarto {{ detachRoom ? detachRoom.room : '' }} deixará de compor a conta master e será reaberto como conta avulsa.</p>
          <v-textarea
            v-model="detachJustification"
            outlined
            rows="3"
            label="Justificativa"
            :rules="[value => !!value || 'Informe a justificativa']"
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="detachDialog = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="warning"
            :loading="detaching"
            :disabled="!detachJustification"
            @click="detachRoomFromAccount()"
          >
            Retornar avulsa
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  mdiArrowLeft, mdiCashPlus, mdiCashCheck, mdiPrinter, mdiCash, mdiCreditCard, mdiCalendarClock, mdiHandshakeOutline, mdiCurrencyUsd,
} from '@mdi/js'
import { mapActions } from 'vuex'
import BarTitleDialog from '@/components/BarTitleDialog.vue'
import SelectPix from '@/components/SelectPix.vue'
import AutocompleteSearchCompany from '@/components/AutocompleteSearchCompany.vue'

export default {
  components: {
    AutocompleteSearchCompany,
    BarTitleDialog,
    SelectPix,
  },
  data: () => ({
    breakdownView: 'projected',
    stateFilter: 'all',
    stateFilters: [
      { value: 'all', label: 'Todos' },
      { value: 'transferred', label: 'Na master' },
      { value: 'individual_closed', label: 'Individual' },
      { value: 'open', label: 'Em aberto' },
    ],
    coverageMode: 'lodging',
    roomLines: {},
    loadingLines: null,
    breakdownBuckets: [
      { key: 'lodging', label: 'Hospedagem (diárias + ISS)' },
      { key: 'extras', label: 'Extras de tarifa' },
      { key: 'products', label: 'Consumo (produtos)' },
      { key: 'services', label: 'Serviços (+ ISS)' },
    ],
    loading: true,
    creating: false,
    savingPayment: false,
    closing: false,
    reopening: false,
    sendingToGestao: false,
    paymentDialog: false,
    closeDialog: false,
    adjustmentDialog: false,
    detachDialog: false,
    removePaymentDialog: false,
    removePaymentTarget: null,
    removePaymentJustification: '',
    removingPayment: false,
    savingAdjustments: false,
    companyDialog: false,
    savingCompany: false,
    newCompany: null,
    companyJustification: '',
    detaching: false,
    printingVoucher: false,
    detachRoom: null,
    detachJustification: '',
    group: null,
    accountData: null,
    responsible: {
      type: 'company',
      item: null,
    },
    payment: {
      value: 0,
      method: 'pix',
      payment_type: 'master_advance',
      date_invoice: null,
      code_operation: '',
      pix_id: null,
    },
    pixError: '',
    codeOperationError: '',
    invoiceDateError: '',
    paymentMethods: [
      { text: 'Dinheiro', value: 'money' },
      { text: 'Pix', value: 'pix' },
      { text: 'Cartão de crédito', value: 'credit_card' },
      { text: 'Cartão de débito', value: 'debit_card' },
      { text: 'Faturado', value: 'invoice' },
      { text: 'B2B', value: 'b2b' },
      { text: 'Outros', value: 'other' },
    ],
    paymentMethodOptions: [
      {
        text: 'Dinheiro', value: 'money', icon: mdiCash, color: 'success',
      },
      {
        text: 'Pix', value: 'pix', image: require('@/assets/images/logos/logo-pix.png'),
      },
      {
        text: 'Cartão Crédito', value: 'credit_card', icon: mdiCreditCard, color: 'primary',
      },
      {
        text: 'Cartão Débito', value: 'debit_card', icon: mdiCreditCard, color: 'success',
      },
      {
        text: 'Faturado', value: 'invoice', icon: mdiCalendarClock, color: 'primary',
      },
      {
        text: 'B2B', value: 'b2b', icon: mdiHandshakeOutline, color: 'warning',
      },
      {
        text: 'Outros', value: 'other', icon: mdiCurrencyUsd, color: 'warning',
      },
    ],
    paymentTypes: [
      { text: 'Adiantamento master', value: 'master_advance' },
      { text: 'Pagamento de fechamento', value: 'master_closing' },
    ],
    adjustments: {
      discount: 0,
      discount_obs: '',
      additional: 0,
      additional_obs: '',
    },
    roomHeaders: [
      { text: 'QUARTO', value: 'room' },
      { text: 'RESERVA', value: 'reserve_id' },
      { text: 'HÓSPEDES', value: 'guests' },
      { text: 'SITUAÇÃO', value: 'financial_state' },
      { text: 'ATUAL', value: 'current_charges', align: 'end' },
      { text: 'PREVISTO', value: 'projected_charges', align: 'end' },
      { text: 'PAGAMENTOS', value: 'payments', align: 'end' },
      { text: 'SALDO ATUAL', value: 'current_balance', align: 'end' },
      { text: 'SALDO PREVISTO', value: 'projected_balance', align: 'end' },
      {
        text: '', value: 'actions', sortable: false, align: 'end',
      },
    ],
    paymentHeaders: [
      { text: 'ORIGEM', value: 'allocation_type' },
      { text: 'MÉTODO', value: 'method' },
      { text: 'DATA', value: 'paid_at' },
      { text: 'VALOR', value: 'amount', align: 'end' },
      {
        text: '', value: 'actions', sortable: false, align: 'end',
      },
    ],
    icons: {
      mdiArrowLeft, mdiCashPlus, mdiCashCheck, mdiPrinter,
    },
  }),
  computed: {
    account() {
      return this.accountData ? this.accountData.account : null
    },
    summary() {
      return this.accountData ? this.accountData.summary : {}
    },
    rooms() {
      const rooms = this.accountData ? this.accountData.rooms : []

      // reserve_id repeats when a room is split into master + avulsa, so give each row its own key
      return rooms.map((room, index) => ({ ...room, room_key: `${room.reserve_id}-${room.accommodation_id || 'r'}-${index}` }))
    },
    filteredRooms() {
      if (this.stateFilter === 'all') return this.rooms
      if (this.stateFilter === 'open') {
        return this.rooms.filter(room => ['preview', 'open'].includes(room.financial_state))
      }

      return this.rooms.filter(room => room.financial_state === this.stateFilter)
    },
    summaryBreakdown() {
      const empty = {
        lodging: 0, extras: 0, products: 0, services: 0, total: 0,
      }
      const breakdown = this.accountData && this.accountData.breakdown

      return (breakdown && breakdown[this.breakdownView]) || empty
    },
    payments() {
      return this.accountData ? this.accountData.payments : []
    },
    totalPayments() {
      return (this.summary.child_payments || 0) + (this.summary.master_payments || 0)
    },
    billingReserveCount() {
      return ((this.group && this.group.reserves) || []).filter(reserve => reserve.billing_checkout_enabled).length
    },
    responsibleName() {
      return this.account ? this.account.responsible_name || 'Responsável não encontrado' : ''
    },
    groupGuests() {
      if (!this.group) return []

      const accommodationGuests = this.group.reserves.flatMap(reserve => (reserve.accommodation || [])
        .flatMap(accommodation => accommodation.guest || []))
      const reserveGuests = this.group.reserves.flatMap(reserve => reserve.guest || [])
      const guests = accommodationGuests.concat(reserveGuests)

      return guests.filter((guest, index) => guests.findIndex(item => item.id === guest.id) === index)
    },
    currentBalanceClass() {
      if (this.summary.current_balance > 0) return 'error--text'
      if (this.summary.current_balance < 0) return 'warning--text'

      return 'success--text'
    },
    accountStatus() {
      if (!this.account) return {}
      if (this.account.status === 'closed_pending_audit') return { label: 'Aguardando auditoria', color: 'warning', textColor: 'black' }
      if (this.account.status === 'sent') return { label: 'Enviada ao Gestão', color: 'info', textColor: 'white' }

      return {
        label: 'Aberta',
        color: 'success',
        textColor: 'white',
      }
    },
    responsibleRecord() {
      if (!this.account) return null

      if (this.account.responsible_type === 'company') {
        return this.group?.company || null
      }

      return this.groupGuests.find(guest => guest.id === this.account.guest_id) || null
    },
    effectiveBalance() {
      return this.summary.effective_balance || 0
    },
    hasRoomInPreview() {
      return this.rooms.some(room => room.financial_state === 'preview')
    },
  },
  watch: {
    'payment.method': function () {
      this.pixError = ''
      this.codeOperationError = ''
      this.invoiceDateError = ''
    },
    'responsible.type': function (type) {
      if (!this.group || this.accountData) return

      this.responsible.item = type === 'company'
        ? this.group.company
        : this.groupGuests[0] || null
    },
  },
  created() {
    this.loadAccount()
    this.loadPix()
  },
  methods: {
    bucketLabel(key) {
      const bucket = this.breakdownBuckets.find(item => item.key === key)

      return bucket ? bucket.label : key
    },
    async loadRoomLines(room) {
      this.loadingLines = room.accommodation_id
      try {
        const response = await this.getGroupAccountRoomLines({
          accountId: this.account.id,
          accommodationId: room.accommodation_id,
        })
        this.$set(this.roomLines, room.accommodation_id, response.data.lines)
      } catch (error) {
        this.$toast.error(error.response?.data?.message || 'Não foi possível carregar os lançamentos do quarto.')
      } finally {
        this.loadingLines = null
      }
    },
    roomFinancialState(room) {
      if (room.financial_state === 'transferred') return { label: 'Na master', color: 'success', textColor: 'white' }
      if (room.financial_state === 'individual_closed') return { label: 'Fechado individual', color: 'info', textColor: 'white' }
      if (room.financial_state === 'outstanding') {
        return room.outstanding_status === 'closed'
          ? { label: 'Avulsa paga', color: 'info', textColor: 'white' }
          : { label: 'Avulsa pendente', color: 'warning', textColor: 'black' }
      }

      return { label: 'Em aberto', color: 'grey lighten-1', textColor: 'black' }
    },
    ...mapActions('reserve', ['getGroupAccount', 'getGroupAccountRoomLines', 'createGroupAccount', 'addGroupAccountPayment', 'removeGroupAccountPayment', 'updateGroupAccountAdjustments', 'changeGroupAccountCompany', 'detachGroupAccountCheckout', 'closeGroupAccount', 'reopenGroupAccount', 'sendGroupAccountToGestao', 'exportGroupAccountVoucher']),
    ...mapActions('select', ['loadPix']),
    loadAccount() {
      this.loading = true
      this.getGroupAccount(this.$route.params.group_id)
        .then(response => {
          this.group = response.data.group
          this.accountData = response.data.account
          if (this.account) {
            this.adjustments = {
              discount: this.summary.discount || 0,
              discount_obs: this.account.discount_obs || '',
              additional: this.summary.additional || 0,
              additional_obs: this.account.additional_obs || '',
            }
          }
          if (!this.accountData) {
            if (this.group.company) {
              this.responsible.type = 'company'
              this.responsible.item = this.group.company
            } else if (this.groupGuests.length) {
              this.responsible.type = 'guest'
              this.responsible.item = this.groupGuests[0]
            } else {
              this.responsible.item = null
            }
          }
        })
        .finally(() => { this.loading = false })
    },
    createAccount() {
      this.creating = true
      this.createGroupAccount({
        groupId: this.$route.params.group_id,
        data: {
          responsible_type: this.responsible.type,
          responsible_id: this.responsible.item.id,
          coverage_mode: this.coverageMode,
        },
      })
        .then(response => {
          this.accountData = response.data
          this.$toast.success('Conta master aberta')
        })
        .finally(() => { this.creating = false })
    },
    openPaymentDialog(type) {
      this.payment.payment_type = type
      this.payment.value = type === 'master_closing' ? this.effectiveBalance : 0
      this.paymentDialog = true
    },
    validatePaymentMethod() {
      this.pixError = ''
      this.codeOperationError = ''
      this.invoiceDateError = ''

      if (this.payment.method === 'pix' && !this.payment.pix_id) {
        this.pixError = 'Chave Pix é obrigatória'

        return false
      }
      if (['credit_card', 'debit_card'].includes(this.payment.method) && !this.payment.code_operation) {
        this.codeOperationError = 'Código de autorização é obrigatório'

        return false
      }
      if (this.payment.method === 'invoice' && !this.payment.date_invoice) {
        this.invoiceDateError = 'Data de vencimento é obrigatória'

        return false
      }

      return true
    },
    addPayment() {
      if (!this.validatePaymentMethod()) return

      this.savingPayment = true
      this.addGroupAccountPayment({
        accountId: this.account.id,
        data: this.payment,
      })
        .then(response => {
          const wasClosingPayment = this.payment.payment_type === 'master_closing'
          this.accountData = response.data
          this.payment = {
            value: 0,
            method: 'pix',
            payment_type: 'master_advance',
            date_invoice: null,
            code_operation: '',
            pix_id: null,
          }
          this.$toast.success('Pagamento registrado')

          if (wasClosingPayment && this.effectiveBalance > 0.01) {
            this.$toast.info('Ainda falta quitar o saldo. Registre a próxima forma de pagamento.')
            this.openPaymentDialog('master_closing')
          } else {
            this.paymentDialog = false
          }
        })
        .finally(() => { this.savingPayment = false })
    },
    saveAdjustments() {
      this.savingAdjustments = true
      this.updateGroupAccountAdjustments({ accountId: this.account.id, data: this.adjustments })
        .then(response => {
          this.accountData = response.data
          this.adjustmentDialog = false
          this.$toast.success('Ajustes atualizados')
        })
        .finally(() => { this.savingAdjustments = false })
    },
    openCompanyDialog() {
      this.newCompany = null
      this.companyJustification = ''
      this.companyDialog = true
    },
    saveCompany(confirmBillingDisable = false) {
      this.savingCompany = true
      this.changeGroupAccountCompany({
        accountId: this.account.id,
        data: {
          company_id: this.newCompany.id,
          justification: this.companyJustification,
          confirm_billing_disable: confirmBillingDisable,
        },
      })
        .then(() => {
          this.companyDialog = false
          this.$toast.success('Empresa responsável atualizada')
          this.loadAccount()
        })
        .catch(error => {
          const data = error.response && error.response.data
          if (data && data.requires_confirmation) {
            this.$swal({
              icon: 'warning',
              title: 'Faturamento será desativado',
              text: data.message,
              showCancelButton: true,
              confirmButtonText: 'Trocar mesmo assim',
              cancelButtonText: 'Cancelar',
            }).then(result => {
              if (result.isConfirmed) this.saveCompany(true)
            })

            return
          }

          // callbackerror skips the global handler, so show the failure here
          this.$swal({
            icon: 'error',
            title: 'Erro',
            html: (data && (data.message || (typeof data === 'string' && data))) || 'Não foi possível trocar a empresa.',
          })
        })
        .finally(() => { this.savingCompany = false })
    },
    openDetachDialog(room) {
      this.detachRoom = room
      this.detachJustification = ''
      this.detachDialog = true
    },
    detachRoomFromAccount() {
      this.detaching = true
      this.detachGroupAccountCheckout({
        checkoutId: this.detachRoom.checkout_id,
        justification: this.detachJustification,
      })
        .then(() => {
          this.detachDialog = false
          this.$toast.success('Quarto retornado para conta avulsa')
          this.loadAccount()
        })
        .finally(() => { this.detaching = false })
    },
    openRemovePaymentDialog(payment) {
      this.removePaymentTarget = payment
      this.removePaymentJustification = ''
      this.removePaymentDialog = true
    },
    removePayment() {
      this.removingPayment = true
      this.removeGroupAccountPayment({
        accountId: this.account.id,
        allocationId: this.removePaymentTarget.id,
        justification: this.removePaymentJustification,
      })
        .then(response => {
          this.accountData = response.data
          this.removePaymentDialog = false
          this.$toast.success('Pagamento removido')
        })
        .finally(() => { this.removingPayment = false })
    },
    confirmCloseAccount() {
      const validationError = this.validateResponsibleForInvoice()
      if (validationError) {
        const isCompany = this.account.responsible_type === 'company'
        this.$swal({
          confirmButtonColor: '#16b1ff',
          cancelButtonColor: '#d33',
          showCancelButton: true,
          confirmButtonText: 'Ir para Cadastros',
          cancelButtonText: 'Cancelar',
          position: 'center',
          html: `<p>${validationError}</p><br><p>Deseja ir para a tela de cadastros para atualizar os dados do ${isCompany ? 'empresa' : 'hóspede'} responsável?</p>`,
          icon: 'warning',
          title: 'Dados Incompletos',
        }).then(result => {
          if (result.isConfirmed) {
            this.$router.push(isCompany ? '/company' : '/guest')
          }
        })

        return
      }

      this.closing = true
      this.closeGroupAccount(this.account.id)
        .then(response => {
          this.accountData = response.data
          this.closeDialog = false
          this.$toast.success('Conta fechada e aguardando auditoria')
        })
        .finally(() => { this.closing = false })
    },
    validateResponsibleForInvoice() {
      const data = this.responsibleRecord
      if (!data) {
        return 'Não foi possível localizar o cadastro do responsável pela conta master.'
      }

      const isCompany = this.account.responsible_type === 'company'
      const missingFields = []

      if (!isCompany && !data.foreigner) {
        if (!data.cpf || data.cpf.replace(/[^\d]/g, '').length !== 11) {
          missingFields.push('CPF')
        } else if (!this.isValidCPF(data.cpf)) {
          missingFields.push('CPF válido')
        }
      } else if (isCompany) {
        if (!data.cpf_cnpj || data.cpf_cnpj.replace(/[^\d]/g, '').length !== 14) {
          missingFields.push('CNPJ')
        }
      }

      if (!data.address || data.address.trim() === '') {
        missingFields.push('Endereço')
      }
      if (!data.number || data.number.trim() === '') {
        missingFields.push('Número')
      }
      if (!data.neighborhood || data.neighborhood.trim() === '') {
        missingFields.push('Bairro')
      }
      if (!data.zipcode || data.zipcode.replace(/[^\d]/g, '').length !== 8) {
        missingFields.push('CEP')
      }
      if (!data.city_id) {
        missingFields.push('Cidade')
      }
      if (!data.state_id) {
        missingFields.push('Estado')
      }
      if (!data.email || !data.email.includes('@')) {
        missingFields.push('Email')
      }
      if ((!data.commercial_phone || data.commercial_phone.trim() === '')
          && (!data.cell_phone || data.cell_phone.trim() === '')) {
        missingFields.push('Telefone de contato')
      }

      if (missingFields.length > 0) {
        return `Os seguintes dados são obrigatórios para emissão da nota fiscal: ${missingFields.join(', ')}`
      }

      return null
    },
    isValidCPF(rawCpf) {
      const cpf = rawCpf.replace(/[^\d]/g, '')

      if (cpf.length !== 11) return false

      let sum = 0
      for (let i = 0; i < 9; i += 1) {
        sum += parseInt(cpf.charAt(i), 10) * (10 - i)
      }
      let remainder = (sum * 10) % 11
      if (remainder === 10 || remainder === 11) remainder = 0
      if (remainder !== parseInt(cpf.charAt(9), 10)) return false

      sum = 0
      for (let i = 0; i < 10; i += 1) {
        sum += parseInt(cpf.charAt(i), 10) * (11 - i)
      }
      remainder = (sum * 10) % 11
      if (remainder === 10 || remainder === 11) remainder = 0
      if (remainder !== parseInt(cpf.charAt(10), 10)) return false

      return true
    },
    reopenAccount() {
      this.reopening = true
      this.reopenGroupAccount(this.account.id)
        .then(response => {
          this.accountData = response.data
          this.$toast.success('Conta master reaberta')
        })
        .finally(() => { this.reopening = false })
    },
    sendToGestao() {
      this.sendingToGestao = true
      this.sendGroupAccountToGestao(this.account.id)
        .then(() => {
          this.$toast.success('Conta master enviada ao Gestão')
          this.loadAccount()
        })
        .finally(() => { this.sendingToGestao = false })
    },
    printVoucher() {
      this.printingVoucher = true
      this.exportGroupAccountVoucher(this.account.id)
        .then(response => {
          const fileUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
          const fileLink = document.createElement('a')
          fileLink.href = fileUrl
          fileLink.setAttribute('download', `voucher-conta-master-${this.account.id}.pdf`)
          document.body.appendChild(fileLink)
          fileLink.click()
          fileLink.remove()
          window.URL.revokeObjectURL(fileUrl)
        })
        .finally(() => { this.printingVoucher = false })
    },
    money(value) {
      return Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    dateTime(value) {
      return value ? new Date(value).toLocaleString('pt-BR') : '-'
    },
    paymentOrigin(type) {
      return {
        child_payment: 'Pagamento do quarto',
        master_advance: 'Adiantamento master',
        master_closing: 'Fechamento master',
      }[type] || type
    },
    paymentMethod(method) {
      return {
        money: 'Dinheiro',
        pix: 'Pix',
        credit_card: 'Cartão de crédito',
        debit_card: 'Cartão de débito',
        invoice: 'Faturado',
        b2b: 'B2B',
        other: 'Outros',
      }[method] || method
    },
  },
}
</script>

<style scoped>
.summary-label { color: #5f6368; font-size: 0.75rem; margin-bottom: 4px; }
.summary-value { font-size: 1.1rem; font-weight: 600; }
.payment-method-card { cursor: pointer; }
.payment-method-card:hover { background: rgb(248, 248, 248); }
.payment-method-card.selected { border-color: #16b1ff; border-width: 2px; }
</style>
